import React, { useEffect } from 'react';
import styled from 'styled-components';
import { isEmpty } from 'lodash';
import { Throbber } from '@lt/components';
import InView, { useInView } from 'react-intersection-observer';
import { useTranslation } from '@lt/localization';

import HotelRenderer from '../../../../containers/HotelRenderer';
import type { Hotels } from '../../../Hotels/types';
import ShowMorePenalizedHotels from '../ShowMorePenalizedHotels';

interface IPenalizedHotels {
  hotels: Hotels;
  isMobile?: boolean;
  isShownPenalizedHotels: boolean;
  handlePenalizedHotelVisibility?: (data: number, isInView: boolean) => void;
  setPenalizedHotelsVisibility: (inView: boolean) => void;
  setShowPenalizedHotels: () => void;
  visibleLimit: number;
}

export const PenalizedHotels = (props: IPenalizedHotels) => {
  const {
    hotels,
    isMobile = false,
    isShownPenalizedHotels,
    setShowPenalizedHotels,
    setPenalizedHotelsVisibility,
    handlePenalizedHotelVisibility,
    visibleLimit,
  } = props;

  const { ref, inView } = useInView({ triggerOnce: false });

  useEffect(() => {
    setPenalizedHotelsVisibility(inView);
  }, [inView, setPenalizedHotelsVisibility]);

  const renderHotels = inView && hotels.length;

  useEffect(() => {
    if (renderHotels && !isShownPenalizedHotels) {
      setShowPenalizedHotels();
    }
  }, [renderHotels, isShownPenalizedHotels, setShowPenalizedHotels]);

  const { t } = useTranslation();

  if (isEmpty(hotels)) return null;

  return (
    <Wrapper ref={ref}>
      <Title>
        {t('search.penalizedHotels.titleFirstRow')}
        {!isMobile && <br />} {t('search.penalizedHotels.titleSecondRow')}
      </Title>

      <LoaderContainer>
        <Loader color="#444" size={30} />
      </LoaderContainer>

      {isShownPenalizedHotels && (
        <ul>
          {hotels.slice(0, visibleLimit).map((hotelInfo, key) => (
            <InView
              key={hotelInfo.tour_id}
              onChange={(isInView) => {
                handlePenalizedHotelVisibility?.(key, isInView);
              }}
            >
              <Item>
                <HotelRenderer
                  index={key}
                  defaultImages={hotelInfo.hotel.images}
                  hotelInfo={hotelInfo}
                  isSmallCard={isMobile}
                />
              </Item>
            </InView>
          ))}

          <ShowMorePenalizedHotels />
        </ul>
      )}
    </Wrapper>
  );
};

const Wrapper = styled.div`
  padding-bottom: 40px;
`;

const LoaderContainer = styled.div`
  position: relative;
`;

const Loader = styled(Throbber)`
  top: 20px;
`;

const Title = styled.div`
  font-weight: bold;
  font-size: 22px;
  color: #17181a;
  margin: 24px 8px 16px;
`;

const Item = styled.li`
  margin-bottom: 8px;
  max-width: 100vw;
  position: relative;
`;
